/**
 * templateHelpers.js
 * Utility helpers for exporting, parsing, validating, and re-indexing OMNIVASO Page Templates.
 */

import { getTenantHandle } from './styleHelpers.js';

export const RESERVED_SLUGS = [
    'admin', 'login', 'articles', 'shop', 'cart', 'search', 
    'product', 'why-omnivaso', 'comparison', 'sites', 'site', 's', 'sitemap.xml'
];

/**
 * Normalizes media upload URLs in imported templates to the active tenant/standalone context
 * @param {string} url 
 * @param {string} [activeTenant]
 * @returns {string}
 */
export const normalizeImportedMediaUrl = (url, activeTenant) => {
    if (!url || typeof url !== 'string') return url;
    const tenant = activeTenant !== undefined ? activeTenant : getTenantHandle();
    return url.replace(/(?:\/?(?:sites|s|site)\/[a-zA-Z0-9_-]+)?\/uploads\//gi, () => {
        if (tenant) {
            return `/s/${tenant}/uploads/`;
        }
        return '/uploads/';
    });
};

/**
 * Trigger browser download of a Page Template JSON file
 * @param {Object} page - The page object from config.pages or active page state
 */
export const exportPageTemplate = (page) => {
    if (!page) return;

    const pageTitle = page.title || page.hero?.title || page.settings?.siteTitle || "Untitled Page";
    const pageSlug = page.slug || "page-template";

    // Normalize sections whether array or object dictionary
    let rawSections = [];
    if (Array.isArray(page.sections)) {
        rawSections = page.sections;
    } else if (page.sections && typeof page.sections === 'object') {
        rawSections = Object.values(page.sections);
    } else if (Array.isArray(page.layout)) {
        rawSections = page.layout;
    }

    const templateData = {
        omnivasoTemplate: true,
        templateVersion: "1.0",
        type: "page_template",
        exportedAt: new Date().toISOString(),
        page: {
            title: pageTitle,
            slug: pageSlug,
            image: page.image || "",
            content: page.content || "",
            hero: page.hero ? JSON.parse(JSON.stringify(page.hero)) : null,
            sections: JSON.parse(JSON.stringify(rawSections))
        }
    };

    const cleanSlug = String(pageSlug).toLowerCase().replace(/[^a-z0-9\-]/g, '-').replace(/-+/g, '-');
    const filename = `${cleanSlug || 'page'}-template.json`;

    const jsonString = JSON.stringify(templateData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Validate and parse raw JSON text or object into a verified Template object
 * @param {string|Object} rawInput 
 * @returns {Object} { valid: boolean, error?: string, template?: Object, page?: Object, stats?: Object }
 */
export const validateAndParseTemplate = (rawInput) => {
    let parsed = null;
    if (typeof rawInput === 'string') {
        try {
            parsed = JSON.parse(rawInput);
        } catch (err) {
            return { valid: false, error: 'Invalid JSON file format. Please check the file syntax.' };
        }
    } else if (typeof rawInput === 'object' && rawInput !== null) {
        parsed = rawInput;
    } else {
        return { valid: false, error: 'Empty or invalid template data.' };
    }

    // Comprehensive extraction of page object from any known OMNIVASO export or template structure
    let pageObj = null;

    if (Array.isArray(parsed)) {
        // Direct array of section objects
        pageObj = {
            title: 'Imported Template',
            slug: 'imported-template',
            hero: null,
            sections: parsed
        };
    } else if (parsed.page && typeof parsed.page === 'object') {
        // Exported template wrapper ({ omnivasoTemplate: true, page: { ... } } or { page: { ... } })
        pageObj = { ...parsed.page };
    } else if (parsed.template && typeof parsed.template === 'object') {
        // Nested under { template: { ... } }
        if (parsed.template.page && typeof parsed.template.page === 'object') {
            pageObj = { ...parsed.template.page };
        } else {
            pageObj = { ...parsed.template };
        }
    } else if (parsed.data && typeof parsed.data === 'object') {
        // Nested under { data: { ... } }
        if (parsed.data.page && typeof parsed.data.page === 'object') {
            pageObj = { ...parsed.data.page };
        } else if (parsed.data.sections || parsed.data.hero) {
            pageObj = { ...parsed.data };
        }
    } else if (parsed.sections || parsed.hero || parsed.title || parsed.layout) {
        // Direct page / layout object
        pageObj = { ...parsed };
    } else if (Array.isArray(parsed.pages) && parsed.pages.length > 0) {
        // Full site backup / export: extract first custom page or generate from home
        pageObj = { ...parsed.pages[0] };
    }

    if (!pageObj) {
        return { valid: false, error: 'Unrecognized template format. The JSON file must contain page layout sections or hero header.' };
    }

    // Normalize sections: ensure it's always a valid array
    let normalizedSections = [];
    if (Array.isArray(pageObj.sections)) {
        normalizedSections = pageObj.sections;
    } else if (pageObj.sections && typeof pageObj.sections === 'object') {
        normalizedSections = Object.values(pageObj.sections);
    } else if (Array.isArray(pageObj.layout)) {
        normalizedSections = pageObj.layout;
    } else if (Array.isArray(parsed.sections)) {
        normalizedSections = parsed.sections;
    }
    pageObj.sections = normalizedSections;

    // Check if hero exists either in pageObj or at root
    if (!pageObj.hero && parsed.hero && typeof parsed.hero === 'object') {
        pageObj.hero = parsed.hero;
    }

    // Count statistics
    const sectionsCount = pageObj.sections.length;
    let columnsCount = 0;
    pageObj.sections.forEach(s => {
        if (Array.isArray(s.columns)) {
            columnsCount += s.columns.length;
        } else if (s.columns && typeof s.columns === 'object') {
            columnsCount += Object.keys(s.columns).length;
        }
    });

    if (sectionsCount === 0 && !pageObj.hero && !pageObj.title && !pageObj.content) {
        return { valid: false, error: 'Template file contains no page sections or hero content.' };
    }

    return {
        valid: true,
        template: parsed,
        page: pageObj,
        stats: {
            title: pageObj.title || 'Untitled Page',
            slug: pageObj.slug || 'imported-page',
            sectionsCount,
            columnsCount,
            exportedAt: parsed.exportedAt || null,
            version: parsed.templateVersion || '1.0'
        }
    };
};

/**
 * Process and re-index an imported page template with unique IDs and unique slug
 * @param {Object} rawPageObj - The raw page object from template
 * @param {string} customTitle - Optional title override
 * @param {string} customSlug - Optional slug override
 * @param {Array} existingPages - List of existing pages in data.pages
 * @returns {Object} newPage object ready to save into data.pages
 */
export const processImportedPage = (rawPageObj, customTitle, customSlug, existingPages = []) => {
    // Support overload: processImportedPage(rawPageObj, existingPages)
    if (Array.isArray(customTitle)) {
        existingPages = customTitle;
        customTitle = undefined;
        customSlug = undefined;
    } else if (Array.isArray(customSlug)) {
        existingPages = customSlug;
        customSlug = undefined;
    }

    const timestamp = Date.now();
    const rawTitle = (typeof customTitle === 'string' && customTitle.trim()) 
        ? customTitle 
        : (rawPageObj?.title || rawPageObj?.hero?.title || "Imported Page");
    const title = String(rawTitle).trim();

    // 1. Generate unique slug
    const rawSlugCandidate = (typeof customSlug === 'string' && customSlug.trim())
        ? customSlug
        : (rawPageObj?.slug || title);

    let baseSlug = String(rawSlugCandidate)
        .toLowerCase()
        .replace(/[\s_/\\]+/g, '-')
        .replace(/[^a-z0-9\-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    if (!baseSlug) baseSlug = `page-${timestamp}`;

    let finalSlug = baseSlug;
    let suffix = 2;
    const existingSlugs = new Set((existingPages || []).map(p => p.slug));

    while (existingSlugs.has(finalSlug) || RESERVED_SLUGS.includes(finalSlug)) {
        finalSlug = `${baseSlug}-${suffix}`;
        suffix++;
    }

    const activeTenant = getTenantHandle();
    const cleanMedia = (u) => normalizeImportedMediaUrl(u, activeTenant);

    // 2. Re-index sections and columns with unique IDs and normalized media paths
    const rawSections = Array.isArray(rawPageObj.sections) 
        ? rawPageObj.sections 
        : (rawPageObj.sections && typeof rawPageObj.sections === 'object' ? Object.values(rawPageObj.sections) : []);

    const reindexedSections = rawSections.map((sec, secIdx) => {
        const randId = Math.random().toString(36).substring(2, 6);
        const newSecId = `s-page-${timestamp}-${secIdx + 1}-${randId}`;

        const rawCols = Array.isArray(sec.columns) 
            ? sec.columns 
            : (sec.columns && typeof sec.columns === 'object' ? Object.values(sec.columns) : []);

        const reindexedCols = rawCols.map((col, colIdx) => {
            const colRand = Math.random().toString(36).substring(2, 5);
            return {
                ...col,
                image: cleanMedia(col.image),
                backgroundImage: cleanMedia(col.backgroundImage),
                foregroundImage: cleanMedia(col.foregroundImage),
                stableId: `c-${newSecId}-${colIdx + 1}-${colRand}`,
                id: `c-${newSecId}-${colIdx + 1}-${colRand}`
            };
        });

        return {
            ...sec,
            backgroundImage: cleanMedia(sec.backgroundImage),
            foregroundImage: cleanMedia(sec.foregroundImage),
            stableId: newSecId,
            id: newSecId,
            columns: reindexedCols
        };
    });

    if (reindexedSections.length === 0) {
        const defaultSecId = `s-page-${timestamp}-1`;
        reindexedSections.push({
            id: defaultSecId,
            stableId: defaultSecId,
            title: "Welcome Section",
            type: "columns",
            backgroundColor: "#ffffff",
            backgroundImage: "",
            minHeight: "auto",
            padding: "4rem 2rem",
            heading: title,
            subHeading: "Customize this section with text, columns, or layout elements.",
            foregroundImage: "",
            columns: []
        });
    }

    // 3. Prepare Hero
    const hero = rawPageObj.hero ? {
        title: title,
        subtitle: rawPageObj.hero.subtitle || "",
        buttons: Array.isArray(rawPageObj.hero.buttons) ? rawPageObj.hero.buttons : [],
        backgroundColor: rawPageObj.hero.backgroundColor || "#2a0a0a",
        textColor: rawPageObj.hero.textColor || "#ffffff",
        backgroundImage: cleanMedia(rawPageObj.hero.backgroundImage || ""),
        foregroundImage: cleanMedia(rawPageObj.hero.foregroundImage || ""),
        imageWidth: rawPageObj.hero.imageWidth || "300px",
        ...rawPageObj.hero,
        backgroundImage: cleanMedia(rawPageObj.hero.backgroundImage || ""),
        foregroundImage: cleanMedia(rawPageObj.hero.foregroundImage || ""),
        title: title // Keep synced with custom title
    } : {
        title: title,
        subtitle: "Imported page template",
        buttons: [],
        backgroundColor: "#2a0a0a",
        textColor: "#ffffff",
        backgroundImage: "",
        foregroundImage: "",
        imageWidth: "300px"
    };

    return {
        id: timestamp,
        title: title,
        slug: finalSlug,
        image: cleanMedia(rawPageObj.image || ""),
        content: rawPageObj.content || "",
        hero: hero,
        sections: reindexedSections
    };
};
